import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Upload, X } from "lucide-react";
import { api, errMsg, eur } from "@/lib/api";
import { Field, inp } from "@/components/admin/AdminUi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const n = (v) => { const x = parseFloat(String(v).replace(",", ".")); return Number.isFinite(x) ? x : 0; };

export function AddFromCatalog({ open, store, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || q.trim().length < 2) return setItems([]);
    setLoading(true);
    const t = setTimeout(() => api.get("/admin/catalog/search", { params: { q, store } }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)), 200);
    return () => clearTimeout(t);
  }, [q, open, store]);
  const add = async (r) => {
    try { await api.post("/admin/stock", { code: r.code, store, price: r.proposed }); toast.success("Articolo aggiunto al magazzino"); setItems((l) => l.map((x) => (x.code === r.code ? { ...x, in_stock: true } : x))); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="wh-catalog-dialog">
        <DialogHeader><DialogTitle>Aggiungi da catalogo</DialogTitle></DialogHeader>
        <input autoFocus data-testid="wh-catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice, descrizione, stampante o barcode (min 2)" className={inp + " h-11"} />
        <div className="max-h-[55vh] overflow-y-auto divide-y">
          {loading && <Loader2 className="w-4 h-4 animate-spin m-2" />}
          {items.map((r) => (
            <div key={r.code} className="py-2 flex items-center gap-3" data-testid={`wh-catalog-row-${r.code}`}>
              <div className="min-w-0 flex-1"><div className="font-mono font-extrabold">{r.code}</div><div className="text-xs text-slate-600 line-clamp-2">{r.description}</div><div className="text-[11px] text-slate-500">{r.supplier} · costo {eur(r.cost)}</div></div>
              <div className="text-right text-sm"><div className="font-bold">{eur(r.proposed)}</div><div className="text-[11px] text-slate-500">proposto</div></div>
              {r.in_stock ? <span className="text-xs font-semibold text-emerald-700 w-24 text-center">a magazzino</span>
                : <button data-testid={`wh-catalog-add-${r.code}`} onClick={() => add(r)} className="press h-9 w-24 rounded bg-brand-yellow text-brand-navy text-sm font-bold">Aggiungi</button>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PrinterMulti({ value, setValue }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => { if (q.length < 2) return setOpts([]); const t = setTimeout(() => api.get("/admin/compat/models", { params: { q, limit: 15 } }).then((r) => setOpts(r.data)).catch(() => {}), 150); return () => clearTimeout(t); }, [q]);
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">{value.map((p) => <span key={p.id} className="text-xs px-2 py-0.5 rounded bg-slate-100 flex items-center gap-1">{p.label}<button onClick={() => setValue(value.filter((x) => x.id !== p.id))}><X className="w-3 h-3" /></button></span>)}</div>
      <input data-testid="wh-new-printer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Aggiungi stampante compatibile (es. xp 2200)" className={inp} />
      {opts.length > 0 && <div className="border rounded max-h-32 overflow-y-auto">{opts.map((o) => <button key={o.id} type="button" data-testid={`wh-new-printer-opt-${o.id}`} onClick={() => { if (!value.find((x) => x.id === o.id)) setValue([...value, o]); setQ(""); }} className="block w-full text-left px-2 py-1 text-sm hover:bg-slate-100">{o.label}</button>)}</div>}
    </div>
  );
}

const EMPTY = { code: "", description: "", cost: "", net: "", price: "", vat: 22, qty: "", min: "", barcode: "", supplier: "", printers: [] };

export function NewArticle({ open, store, onClose, onDone }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(EMPTY); }, [open]);
  const save = async () => {
    try {
      await api.post("/admin/stock/new", { store, code: f.code, description: f.description, cost: n(f.cost), price: n(f.price), vat: +f.vat, qty: n(f.qty), min: n(f.min), barcode: f.barcode, supplier: f.supplier, printer_ids: f.printers.map((p) => p.id) });
      toast.success("Articolo creato"); onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const I = (k, label, extra = {}) => <Field label={label} {...extra}><input data-testid={`wh-new-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value, ...(k === "net" ? { price: (n(e.target.value) * (1 + f.vat / 100)).toFixed(2) } : k === "price" ? { net: (n(e.target.value) / (1 + f.vat / 100)).toFixed(2) } : {}) })} className={inp} /></Field>;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="wh-new-dialog">
        <DialogHeader><DialogTitle>Nuovo articolo</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {I("code", "Codice *")}{I("barcode", "Barcode")}{I("description", "Descrizione *", { wide: true })}
          {I("cost", "Prezzo acquisto €")}{I("net", "Vendita imponibile €")}{I("price", "Prezzo ivato €")}
          <Field label="IVA %"><select value={f.vat} onChange={(e) => setF({ ...f, vat: +e.target.value, price: (n(f.net) * (1 + e.target.value / 100)).toFixed(2) })} className={inp}>{[22, 10, 4, 0].map((v) => <option key={v}>{v}</option>)}</select></Field>
          {I("qty", "Giacenza iniziale")}{I("min", "Scorta minima")}{I("supplier", "Fornitore", { wide: true })}
          <Field label="Stampanti compatibili" wide><PrinterMulti value={f.printers} setValue={(v) => setF({ ...f, printers: v })} /></Field>
        </div>
        <button data-testid="wh-new-save" onClick={save} className="press h-11 rounded-md bg-brand-yellow text-brand-navy font-bold flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Crea articolo</button>
      </DialogContent>
    </Dialog>
  );
}

export function StockImport({ open, store, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setPrev(null); } }, [open]);
  const run = async (commit) => {
    if (!file) return toast.error("Scegli un file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("store", store); fd.append("commit", commit);
      const { data } = await api.post("/admin/stock/import", fd);
      setPrev(data);
      if (commit) { toast.success(`${data.valid} giacenze aggiornate`); onDone(); onClose(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="wh-import-dialog">
        <DialogHeader><DialogTitle>Importa giacenze (CSV / Excel)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-600">Colonne: codice, giacenza, scorta_minima (facoltativa), barcode (facoltativo). Ogni riga crea un movimento "Inventario fisico".</p>
        <input data-testid="wh-import-file" type="file" accept=".xlsx,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setPrev(null); }} className="text-sm" />
        {prev && <div className="text-sm rounded border bg-slate-50 p-2" data-testid="wh-import-preview"><b>{prev.valid}</b> righe valide{prev.errors.length > 0 && <div className="text-xs text-rose-700 max-h-28 overflow-y-auto mt-1">{prev.errors.map((e) => <div key={e}>{e}</div>)}</div>}</div>}
        <div className="flex gap-2">
          <button data-testid="wh-import-preview-btn" onClick={() => run(false)} disabled={busy} className="press h-10 px-4 rounded border text-sm font-semibold">Anteprima</button>
          <button data-testid="wh-import-commit-btn" onClick={() => run(true)} disabled={busy || !prev?.valid} className="press h-10 px-4 rounded bg-brand-yellow text-brand-navy text-sm font-bold flex items-center gap-1 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Conferma import</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
